import { CSSProperties, forwardRef, ForwardedRef, useMemo } from 'react'
import { renderToString } from 'react-dom/server'
import ControlSize, { controlHeight } from '../../../types/control-size'
import Gradient from '../../../types/gradient'
import Icon, { iconComponent } from '../../../types/icons'
import { ButtonProps } from '../button'
import { Button, Inverted } from './style'

export interface IconButtonProps extends Omit<ButtonProps, 'iconLocation' | 'textColor'> {
    icon: Icon
    iconColor?: string
    iconScale?: number
    inverted?: boolean
    size?: ControlSize
    style?: CSSProperties
    gradient?: Gradient
}

const IconButton = (
    {
        color,
        icon,
        iconColor,
        iconScale = 1,
        gradient,
        inverted = false,
        size = 'small',
        style,
        ...rest
    }: IconButtonProps,
    ref: ForwardedRef<HTMLButtonElement>
) => {
    const IconComponent = useMemo(() => iconComponent(icon), [icon])
    const iconHtml = useMemo(() => renderToString(<IconComponent />), [IconComponent])
    const iconSize = useMemo(() => Math.round(controlHeight(size) * 0.5 * iconScale), [iconScale, size])
    const iconOffset = useMemo(() => ((controlHeight(size) - iconSize) / 2) * iconScale, [iconSize])

    const maskCss = `url(data:image/svg+xml,${encodeURIComponent(
        iconHtml
    )}) ${iconOffset}px ${iconOffset}px/${iconSize}px ${iconSize}px no-repeat`

    const maskStyle = {
        WebkitMask: maskCss,
        Mask: maskCss,
    }

    return (
        <Button
            {...rest}
            color={inverted ? 'transparent' : color}
            gradient={inverted ? undefined : gradient}
            ref={ref}
            size={size}
            style={style}
        >
            {inverted ? (
                <Inverted color={color} gradient={gradient} iconColor={iconColor} style={maskStyle} />
            ) : (
                <IconComponent color={iconColor} style={{ height: iconSize, width: iconSize }} />
            )}
        </Button>
    )
}

export default forwardRef(IconButton)
